import React, { useState, useRef, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Briefcase, User, CheckCircle2, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { saveSubmission } from '../services/databaseService';
import { sendViaWeb3Forms } from '../services/web3formsService';
import { Link, useNavigate, useParams } from 'react-router-dom';
type FormType = 'employeur' | 'professionnel' | null;
// Brand colors
const NAVY = '#1E3A5F';
const GOLD = '#D4A574';
// URL segment <-> form mapping. /inscription/Employeur and /inscription/Travailleur
const PATH_EMPLOYEUR = '/inscription/Employeur';
const PATH_TRAVAILLEUR = '/inscription/Travailleur';
function formFromParam(profil?: string): FormType {
  const value = (profil || '').toLowerCase();
  if (value === 'employeur') return 'employeur';
  if (value === 'travailleur') return 'professionnel';
  return null;
}
export function InscriptionPage() {
  const {
    profil
  } = useParams<{
    profil?: string;
  }>();
  const navigate = useNavigate();
  const activeForm = formFromParam(profil);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consentAccepted) return;
    setIsSubmitting(true);
    const formData = new FormData(formRef.current!);
    const data: Record<string, any> = {};
    formData.forEach((value, key) => {
      if (data[key]) {
        if (Array.isArray(data[key])) {
          data[key].push(value);
        } else {
          data[key] = [data[key], value];
        }
      } else {
        data[key] = value;
      }
    });
    // Format checkboxes
    const interet = formData.getAll('interet').join(', ');
    const metiers = formData.getAll('metiers').join(', ');
    let submission: any = {};
    if (activeForm === 'employeur') {
      submission = {
        type: 'promaison_employeur',
        nomComplet: data.nomComplet || '',
        telephone: data.telephone || '',
        email: data.email || '',
        quartier: data.quartier || '',
        interet: interet || '',
        disponibilite: data.disponibilite || '',
        commentaire: data.commentaire || '',
        consentement: consentAccepted ? 'Oui' : 'Non'
      };
    } else if (activeForm === 'professionnel') {
      submission = {
        type: 'promaison_professionnel',
        nomComplet: data.nomComplet || '',
        telephone: data.telephone || '',
        age: data.age || '',
        quartier: data.quartier || '',
        metiers: metiers || '',
        experience: data.experience || '',
        disponibilite: data.disponibilite || '',
        consentement: consentAccepted ? 'Oui' : 'Non'
      };
    }
    try {
      saveSubmission(submission);
      // Prepare fields for Web3Forms
      const fields: Record<string, string> = {
        ...submission
      };
      delete fields.type;
      await sendViaWeb3Forms({
        type: submission.type,
        fields
      });
      setIsSubmitting(false);
      setIsSuccess(true);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error saving submission:', error);
      setIsSubmitting(false);
      // Show success anyway to not block user
      setIsSuccess(true);
      window.scrollTo(0, 0);
    }
  };
  const resetForm = () => {
    setIsSuccess(false);
    setConsentAccepted(false);
    navigate('/inscription');
  };
  // Switching profile via the URL clears any previous submission state
  useEffect(() => {
    setIsSuccess(false);
    setConsentAccepted(false);
  }, [profil]);
  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent outline-none transition-all bg-gray-50/50';
  const labelClass = 'text-sm font-semibold text-gray-700 mb-1.5 block';
  return <Layout data-id="element-1526" data-mp="1kyd4jf6mdghq">
      <SEO title={activeForm === 'employeur' ? 'Inscription PRO MAISON — Employeur | ADCC' : activeForm === 'professionnel' ? 'Inscription PRO MAISON — Travailleur | ADCC' : 'Inscription PRO MAISON | ADCC'} description="Inscrivez-vous au parcours PRO MAISON. Que vous soyez employeur ou professionnel(le) des services à domicile, rejoignez le programme." url={activeForm === 'employeur' ? 'https://adcc.africa/inscription/Employeur' : activeForm === 'professionnel' ? 'https://adcc.africa/inscription/Travailleur' : 'https://adcc.africa/inscription'} data-id="element-1527" data-mp="1ky4tlsu24l96" />

      <div className="min-h-screen bg-gray-50 pb-20" data-id="element-1528" data-mp="eg7wlpxw0y9x">
        {/* Header Mobile-First */}
        <div className="bg-white border-b border-gray-100 pt-8 pb-6 px-4 text-center sticky top-0 z-10 shadow-sm" data-id="element-1529" data-mp="ik5xa52v2h53">
          <div className="max-w-3xl mx-auto" data-id="element-1530" data-mp="ay0o0hyy5joy">
            <img src="https://cdn.magicpatterns.com/uploads/xq7xorXDBCdoHQja5rTP8f/PROMAISON_logo_extrait.png" alt="PRO MAISON" className="h-16 w-auto mx-auto mb-3 object-contain" data-id="element-1531" data-mp="61a8bvr20q42" />
            <p className="text-sm font-bold tracking-widest uppercase mb-2" style={{
            color: GOLD
          }} data-id="element-1532" data-mp="fzhls4m4wq1b">
              La dignité par la compétence
            </p>
            <p className="text-gray-600 text-sm leading-relaxed max-w-lg mx-auto" data-id="element-1533" data-mp="3jdu44vpd8t5h">
              Rejoignez le parcours certifiant PRO MAISON. Les
              professionnel(le)s accompagné(e)s seront mis en avant sur la
              future plateforme DIGNWORK (lancement prévu en octobre 2026).
            </p>
          </div>
        </div>

        <div className="max-w-xl mx-auto px-4 pt-8" data-id="element-1534" data-mp="30pyuno0se1i6">
          {isSuccess ? <Card className="border-none shadow-xl text-center overflow-hidden" data-id="element-1535" data-mp="3qdkuzcknt7lm">
              <div className="h-2 w-full bg-green-500" data-id="element-1536" data-mp="3uewm5mj6m0vx"></div>
              <CardContent className="p-8 md:p-10" data-id="element-1537" data-mp="3udd5vvxizpu5">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1538" data-mp="30iddcb83dzn">
                  <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-1539" data-mp="7zlckg4iupzp" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1540" data-mp="37eythwl8deh6">
                  {activeForm === 'employeur' ? 'Merci !' : 'Merci pour votre inscription !'}
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-1541" data-mp="3gedgun63p8xg">
                  {activeForm === 'employeur' ? "Un membre de l'équipe ADCC vous contactera prochainement." : "L'équipe ADCC vous recontactera pour la suite du parcours PRO MAISON."}
                </p>
                <Button onClick={resetForm} className="w-full py-4 text-base" style={{
              backgroundColor: NAVY
            }} data-id="element-1542" data-mp="10mftj324r78f">
                  Retour à l'accueil
                </Button>
              </CardContent>
            </Card> : !activeForm ? <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]" data-id="element-1543" data-mp="26hotgnzynwiv">
              <h1 className="text-xl font-bold text-center text-gray-900 mb-6" data-id="element-1544" data-mp="22fk0zsl915xt">
                Sélectionnez votre profil :
              </h1>

              {/* Carte Employeur */}
              <Link to={PATH_EMPLOYEUR} className="block w-full group text-left bg-white rounded-2xl p-6 shadow-sm hover:shadow-md border-2 border-transparent hover:border-[#1E3A5F] transition-all" data-id="element-1545" data-mp="1xiju5fgkrz5n">
                <div className="flex items-start gap-4" data-id="element-1546" data-mp="3mvnbzr2upy5t">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors" style={{
                backgroundColor: '#EEF4FB',
                color: NAVY
              }} data-id="element-1547" data-mp="3mt14tsjvhko4">
                    <Briefcase className="w-6 h-6" data-id="element-1548" data-mp="2vrvsuqts99w2" />
                  </div>
                  <div data-id="element-1549" data-mp="2hy4qq3idssad">
                    <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1550" data-mp="2hwto470o9qku">
                      Je suis employeur / gérant(e) de résidence
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed" data-id="element-1551" data-mp="27yvqiswxahfk">
                      Vous employez ou cherchez du personnel de maison qualifié.
                    </p>
                  </div>
                </div>
              </Link>

              {/* Carte Professionnel */}
              <Link to={PATH_TRAVAILLEUR} className="block w-full group text-left bg-white rounded-2xl p-6 shadow-sm hover:shadow-md border-2 border-transparent hover:border-[#D4A574] transition-all" data-id="element-1552" data-mp="232f1zxlta11y">
                <div className="flex items-start gap-4" data-id="element-1553" data-mp="17o8d343rigx">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors" style={{
                backgroundColor: '#FDF8F3',
                color: GOLD
              }} data-id="element-1554" data-mp="1lsuq8m99s184">
                    <User className="w-6 h-6" data-id="element-1555" data-mp="1qqcrrrkyje63" />
                  </div>
                  <div data-id="element-1556" data-mp="25f96g7hirpfo">
                    <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1557" data-mp="25epl0gsnuiww">
                      Je suis professionnel(le)
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed" data-id="element-1558" data-mp="2egqhvcjzf2y1">
                      Vous travaillez (ou voulez travailler) dans l'entretien,
                      la cuisine, la garde d'enfants...
                    </p>
                  </div>
                </div>
              </Link>
            </div> : <div className="animate-[popIn_0.3s_ease-out]" data-id="element-1559" data-mp="f4ed2s19cpl0">
              <Link to="/inscription" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-id="element-1560" data-mp="f5z78gjnu6ic">
                <ArrowLeft className="w-4 h-4" data-id="element-1561" data-mp="1urh762xgd250" />
                Changer de profil
              </Link>

              <Card className="border-none shadow-xl overflow-hidden" data-id="element-1562" data-mp="1psnqy2qs6x15">
                <div className="h-2 w-full" style={{
              backgroundColor: activeForm === 'employeur' ? NAVY : GOLD
            }} data-id="element-1563" data-mp="1puh0pqwoxxdd"></div>
                <CardContent className="p-6 md:p-8" data-id="element-1564" data-mp="2ijflkvs77dmd">
                  <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100" data-id="element-1565" data-mp="2dp1wfb44q2q5">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: activeForm === 'employeur' ? '#EEF4FB' : '#FDF8F3',
                  color: activeForm === 'employeur' ? NAVY : GOLD
                }} data-id="element-1566" data-mp="28q6zz6d8ofnk">
                      {activeForm === 'employeur' ? <Briefcase className="w-5 h-5" data-id="element-1567" data-mp="3drzyge9fvkt9" /> : <User className="w-5 h-5" data-id="element-1568" data-mp="3inyglk46c6l8" />}
                    </div>
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-1569" data-mp="3ms64jf2t5iie">
                      {activeForm === 'employeur' ? 'Formulaire Employeur' : 'Formulaire Professionnel'}
                    </h2>
                  </div>

                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-1570" data-mp="5bobyb2n8gr3">
                    {/* --- FORMULAIRE EMPLOYEUR --- */}
                    {activeForm === 'employeur' && <>
                        <div data-id="element-1571" data-mp="jawg7idnyf25">
                          <label className={labelClass} data-id="element-1572" data-mp="j81vrqungu8q">Nom complet *</label>
                          <input type="text" name="nomComplet" required className={inputClass} placeholder="Votre nom et prénom" data-id="element-1573" data-mp="2fdsowphyyvt2" />
                        </div>
                        <div data-id="element-1574" data-mp="2kaj6jh9f9zgl">
                          <label className={labelClass} data-id="element-1575" data-mp="2z0qpdy3cdkht">
                            Téléphone (WhatsApp) *
                          </label>
                          <input type="tel" name="telephone" required className={inputClass} placeholder="+229..." data-id="element-1576" data-mp="2u1nie83sx477" />
                        </div>
                        <div data-id="element-1577" data-mp="1dq02dfu9xyzn">
                          <label className={labelClass} data-id="element-1578" data-mp="18p2kz5h398z7">Email</label>
                          <input type="email" name="email" className={inputClass} placeholder="votre@email.com (optionnel)" data-id="element-1579" data-mp="18qx90snnph7i" />
                        </div>
                        <div data-id="element-1580" data-mp="3bjm3tdhivt4l">
                          <label className={labelClass} data-id="element-1581" data-mp="3flfwozlh7su7">
                            Quartier / Résidence *
                          </label>
                          <input type="text" name="quartier" required className={inputClass} placeholder="Ex: Haie Vive" data-id="element-1582" data-mp="31kwtq6vvbcyw" />
                        </div>
                        <div data-id="element-1583" data-mp="2wmvbtpa6hjp0">
                          <label className={labelClass} data-id="element-1584" data-mp="qtn7y2dr5aic">
                            Je suis intéressé(e) par : *
                          </label>
                          <div className="space-y-3 mt-2" data-id="element-1585" data-mp="utxdxf1jo0x7">
                            {['Faire former mon personnel actuel', 'Recruter du personnel certifié', 'Les deux'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-1586" data-mp="3sa7tgvvjijqn">
                                <input type="checkbox" name="interet" value={option} className="w-5 h-5 text-[#1E3A5F] border-gray-300 rounded focus:ring-[#1E3A5F]" data-id="element-1587" data-mp="3ek4fstaq7yct" />
                                <span className="text-sm text-gray-700" data-id="element-1588" data-mp="2zmbx5s9vutue">
                                  {option}
                                </span>
                              </label>)}
                          </div>
                        </div>
                        <div data-id="element-1589" data-mp="1f3rmu8rlwqlf">
                          <label className={labelClass} data-id="element-1590" data-mp="1f75otfwcyxsc">Disponibilité *</label>
                          <select name="disponibilite" required className={inputClass} data-id="element-1591" data-mp="1k4p8wwfqm4s7">
                            <option value="" data-id="element-1592" data-mp="rhbgnbmzhxef">Sélectionnez...</option>
                            <option value="Maintenant" data-id="element-1593" data-mp="wfmqhmda47wn">Maintenant</option>
                            <option value="À la rentrée (septembre)" data-id="element-1594" data-mp="11dxydrk0k8c8">
                              À la rentrée (septembre)
                            </option>
                            <option value="Pas encore décidé" data-id="element-1595" data-mp="165pjvnobbnvi">
                              Pas encore décidé
                            </option>
                          </select>
                        </div>
                        <div data-id="element-1596" data-mp="dlqwydzflb5y">
                          <label className={labelClass} data-id="element-1597" data-mp="5mw01pduwnhb">
                            Commentaire libre
                          </label>
                          <textarea name="commentaire" rows={3} className={inputClass} placeholder="Précisions sur vos besoins (optionnel)..." data-id="element-1598" data-mp="1lasmjk3d1ei"></textarea>
                        </div>
                      </>}

                    {/* --- FORMULAIRE PROFESSIONNEL --- */}
                    {activeForm === 'professionnel' && <>
                        <div data-id="element-1599" data-mp="3stewfjwlg28k">
                          <label className={labelClass} data-id="element-1600" data-mp="3nt0os2ywmub2">Nom complet *</label>
                          <input type="text" name="nomComplet" required className={inputClass} placeholder="Votre nom et prénom" data-id="element-1601" data-mp="3nv3ml96atrqc" />
                        </div>
                        <div data-id="element-1602" data-mp="2pz9fkd6eml35">
                          <label className={labelClass} data-id="element-1603" data-mp="2q0kfu30n5api">
                            Téléphone (WhatsApp) *
                          </label>
                          <input type="tel" name="telephone" required className={inputClass} placeholder="+229..." data-id="element-1604" data-mp="2c1t3om5c9z40" />
                        </div>
                        <div data-id="element-1605" data-mp="2uym0gsdpti33">
                          <label className={labelClass} data-id="element-1606" data-mp="2uzdx86j1tluy">
                            Date de naissance ou âge
                          </label>
                          <input type="text" name="age" className={inputClass} placeholder="Ex: 25 ans ou 12/05/1998 (optionnel)" data-id="element-1607" data-mp="2zvuh2oj7te28" />
                        </div>
                        <div data-id="element-1608" data-mp="1sd1r4v3zubry">
                          <label className={labelClass} data-id="element-1609" data-mp="1nla7l4j996bb">
                            Quartier de résidence *
                          </label>
                          <input type="text" name="quartier" required className={inputClass} placeholder="Ex: Akpakpa" data-id="element-1610" data-mp="1imp7r67x0one" />
                        </div>
                        <div data-id="element-1611" data-mp="26cb3tkszqnr6">
                          <label className={labelClass} data-id="element-1612" data-mp="21ceztlkamwbm">
                            Métier(s) qui m'intéresse(nt) : *
                          </label>
                          <div className="grid grid-cols-1 gap-3 mt-2" data-id="element-1613" data-mp="1xa1uuzddf2fw">
                            {['Entretien du domicile'].map(metier => <label key={metier} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-1614" data-mp="lvoba6pnh95o">
                                <input type="checkbox" name="metiers" value={metier} className="w-5 h-5 text-[#D4A574] border-gray-300 rounded focus:ring-[#D4A574]" data-id="element-1615" data-mp="31kv9ojbtduok" />
                                <span className="text-sm text-gray-700" data-id="element-1616" data-mp="3l8ceu6xttb9e">
                                  {metier}
                                </span>
                              </label>)}
                          </div>
                        </div>
                        <div data-id="element-1617" data-mp="3u6wq4n124inl">
                          <label className={labelClass} data-id="element-1618" data-mp="1uwoo2h4hwy99">Expérience *</label>
                          <select name="experience" required className={inputClass} data-id="element-1619" data-mp="1zv9oagjqzhxp">
                            <option value="" data-id="element-1620" data-mp="1onm60snhr7hb">Sélectionnez...</option>
                            <option value="Aucune expérience" data-id="element-1621" data-mp="1onvugbsw16kw">
                              Aucune expérience
                            </option>
                            <option value="Moins de 2 ans" data-id="element-1622" data-mp="1ydxl9bcml33i">
                              Moins de 2 ans
                            </option>
                            <option value="2 à 5 ans" data-id="element-1623" data-mp="22ehew4xzpokw">2 à 5 ans</option>
                            <option value="Plus de 5 ans" data-id="element-1624" data-mp="27datk0p0jlmn">Plus de 5 ans</option>
                          </select>
                        </div>
                        <div data-id="element-1625" data-mp="2cb40ivy54jnz">
                          <label className={labelClass} data-id="element-1626" data-mp="2hc02h3yxlvqr">
                            Disponibilité pour le parcours *
                          </label>
                          <select name="disponibilite" required className={inputClass} data-id="element-1627" data-mp="i0qoyshfzmu6">
                            <option value="" data-id="element-1628" data-mp="30a3dpmcpie38">Sélectionnez...</option>
                            <option value="Disponible maintenant" data-id="element-1629" data-mp="2vhk0iqhxlkyy">
                              Disponible maintenant
                            </option>
                            <option value="Disponible à la rentrée" data-id="element-1630" data-mp="2rdu97t9vgpa8">
                              Disponible à la rentrée
                            </option>
                          </select>
                        </div>
                      </>}

                    {/* Consentement (Commun) */}
                    <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 mt-8" data-id="element-1631" data-mp="3a6g3wa91m9t8">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-1632" data-mp="357vd11fol2hg">
                        <input type="checkbox" required checked={consentAccepted} onChange={e => setConsentAccepted(e.target.checked)} className="mt-1 w-5 h-5 text-[#1E3A5F] border-gray-300 rounded focus:ring-[#1E3A5F]" data-id="element-1633" data-mp="359zdtaia9ldi" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-1634" data-mp="207x45l5p6dm6">
                          J'accepte que mes données soient traitées par l'ADCC
                          dans le cadre du programme PRO MAISON, conformément à
                          la loi béninoise n°2017-20 sur la protection des
                          données à caractère personnel. *
                        </span>
                      </label>
                    </div>

                    <Button type="submit" className="w-full py-4 text-base shadow-lg" disabled={isSubmitting || !consentAccepted} style={{
                  backgroundColor: activeForm === 'employeur' ? NAVY : GOLD
                }} data-id="element-1635" data-mp="13j8qf110lcik">
                      {isSubmitting ? <span className="flex items-center justify-center gap-2" data-id="element-1636" data-mp="ey2x7ugfw6mf">
                          <Loader2 className="w-5 h-5 animate-spin" data-id="element-1637" data-mp="9zj9ppm76mry" />
                          Envoi en cours...
                        </span> : activeForm === 'employeur' ? 'Envoyer ma demande' : "M'inscrire au programme"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>}

          {/* Footer Contact Info */}
          <div className="mt-12 text-center text-sm text-gray-500 pb-8" data-id="element-1638" data-mp="17lp173tkxd9t">
            <p className="mb-1" data-id="element-1639" data-mp="12mvlr5uqfc70">Besoin d'aide ? Contactez-nous :</p>
            <p className="font-medium text-gray-700" data-id="element-1640" data-mp="1gmwo6pqnarol">
              <a href="mailto:contact@adcc.africa" className="hover:underline" data-id="element-1641" data-mp="1gng460wqj5zz">
                contact@adcc.africa
              </a>{' '}
              |{' '}
              <a href="tel:+22901901175555" className="hover:underline" data-id="element-1642" data-mp="364k1zp996t3f">
                +229 01 90 11 75 55
              </a>
            </p>
          </div>
        </div>
      </div>
    </Layout>;
}